import React, { forwardRef } from "react";
import { StyleSheet, View } from "react-native";
import { TextInput as Input, useTheme } from "react-native-paper";
import { AntDesign, MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemeType } from "../../theme/theme";

/**
 * TextInput
 *
 * HOC for wrapping the TextInput component from react-native-paper.
 * We add a right-side icon when an error message shows.
 *
 * Notice: `iconName` must be a MaterialCommunityIcon as of the current implemtation.
 */

interface ITextInput {
  onChangeText: Function;
  onFocus?: Function;
  value?: any;
  style?: any;
  error?: any;
  disabled?: boolean;
  multiline?: any;
  numberOfLines?: number;
  iconName?: any;
  keyboardType?: any;
  maxLength?: any;
  mode?: any;
  render?: any;
  testID?: any;
  accessibilityId?: any;
  placeholder?: any;
  returnKeyType?:
    | "default"
    | "none"
    | "done"
    | "go"
    | "next"
    | "search"
    | "send"
    | "previous"
    | "google"
    | "join"
    | "route"
    | "yahoo"
    | "emergency-call"
    | undefined;
  inputAccessoryViewID?: string;
  onSubmitEditing?: any;
  accessibilityLabel: string | null;
}

export const TextInput = forwardRef(
  (
    {
      style,
      error,
      render,
      value,
      disabled,
      iconName,
      testID,
      accessibilityId,
      multiline = false,
      numberOfLines = 1,
      keyboardType = "default",
      maxLength,
      mode = "outlined",
      onChangeText,
      onFocus,
      placeholder,
      returnKeyType,
      inputAccessoryViewID,
      onSubmitEditing,
      accessibilityLabel,
    }: ITextInput,
    ref: any
  ) => {
    const { iconSize, fontSize, spacing, colors } = useTheme() as ThemeType;
    const isIconVisible = Boolean(iconName || error);

    const styles = StyleSheet.create({
      container: {
        display: "flex",
        position: "relative",
      },
      iconContainer: {
        display: "flex",
        position: "absolute",
        top: "17%", // there is somehow margin added to the top of the Input so this counteracts it
        right: spacing.small,
        height: spacing.huge,
        alignItems: "center",
        justifyContent: "center",
      },
      icon: {
        width: iconSize.small,
        height: iconSize.small,
      },
      errorText: {
        fontSize: fontSize.small,
        color: colors.error,
        marginTop: spacing.tiny,
      },
    });

    style = {
      ...style,
      backgroundColor: disabled ? colors.lightGrey : colors.white,
    };

    return (
      <>
        <View style={styles.container}>
          <Input
            accessible={true}
            accessibilityLabel={accessibilityLabel}
            style={style}
            error={error}
            mode={mode}
            value={value}
            render={render}
            disabled={disabled}
            testID={testID}
            accessibilityId={accessibilityId}
            multiline={multiline}
            numberOfLines={numberOfLines}
            keyboardType={keyboardType}
            maxLength={maxLength}
            placeholder={placeholder}
            onFocus={onFocus}
            onChangeText={onChangeText}
            ref={ref}
            returnKeyType={returnKeyType}
            inputAccessoryViewID={inputAccessoryViewID}
            onSubmitEditing={onSubmitEditing}
          />
          {isIconVisible && (
            <View style={styles.iconContainer}>
              {error ? (
                <AntDesign
                  style={styles.icon}
                  name={"exclamationcircle"}
                  size={iconSize.small}
                  color="red"
                />
              ) : (
                <MaterialCommunityIcons
                  style={styles.icon}
                  name={iconName}
                  size={iconSize.small}
                  color={disabled ? colors.darkGrey : colors.text}
                />
              )}
            </View>
          )}
        </View>
      </>
    );
  }
);
